import { useState } from 'react'
import { Image as ImageIcon, Eye, Edit3, Save, Loader2, Sparkles, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useUploadKbImage } from '@/queries/kb'
import { api } from '@/lib/api'
import { toast } from 'sonner'
import type { KbArticle } from '@/types/kb'

interface Props {
  initial?: Partial<KbArticle>
  articleId?: string
  onSave: (values: { title: string; subtitle: string; content: string; isPublished: boolean }) => Promise<unknown>
  saving?: boolean
}

interface ImageRef {
  s3Key: string
  filename: string
  inlineMarkdown: string
  resolvedUrl: string
}

function imageMarkdownFor(filename: string, s3Key: string): string {
  // We point to the api endpoint with json=1 so the rendered preview can resolve
  // to the presigned URL. The bare endpoint also works as the canonical link.
  const apiBase = import.meta.env.VITE_API_URL ?? ''
  return `${apiBase}/kb-articles/images/${encodeURIComponent(s3Key)}`
}

export function KbEditor({ initial, articleId, onSave, saving }: Props) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [subtitle, setSubtitle] = useState(initial?.subtitle ?? '')
  const [content, setContent] = useState(initial?.content ?? '')
  const [isPublished, setIsPublished] = useState(initial?.isPublished ?? false)
  const [genOpen, setGenOpen] = useState(false)
  const upload = useUploadKbImage(articleId)

  async function handleImageInsert(file: File): Promise<ImageRef | null> {
    if (file.size > 25 * 1024 * 1024) {
      toast.error(`${file.name} is over 25MB.`)
      return null
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Only images are allowed.')
      return null
    }
    try {
      const meta = await upload.mutateAsync(file)
      const url = imageMarkdownFor(meta.filename, meta.s3Key)
      const md = `![${meta.filename}](${url})`
      setContent(prev => `${prev}\n\n${md}\n\n`)
      toast.success('Image inserted')
      return {
        s3Key: meta.s3Key,
        filename: meta.filename,
        inlineMarkdown: md,
        resolvedUrl: meta.url,
      }
    } catch (err) {
      toast.error((err as Error).message ?? 'Upload failed')
      return null
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) {
      toast.error('Title is required')
      return
    }
    await onSave({ title: title.trim(), subtitle: subtitle.trim(), content, isPublished })
  }

  return (
    <form onSubmit={handleSubmit} className="container mx-auto max-w-4xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{initial?.title ? 'Edit article' : 'New article'}</h1>
        <Button type="button" variant="outline" size="sm" onClick={() => setGenOpen(true)}>
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          Generate with AI
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="kb-title">Title</Label>
        <Input id="kb-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="How to do the thing" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="kb-subtitle">Subtitle (optional)</Label>
        <Input id="kb-subtitle" value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="A one-line summary" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Content (Markdown)</Label>
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
            <span className="inline-flex items-center gap-1.5 rounded border border-border px-2.5 py-1 hover:bg-muted">
              {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
              Insert image
            </span>
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={async e => {
                const file = e.target.files?.[0]
                if (file) await handleImageInsert(file)
                e.target.value = ''
              }}
            />
          </label>
        </div>

        <Tabs defaultValue="write">
          <TabsList>
            <TabsTrigger value="write"><Edit3 className="mr-1.5 h-3.5 w-3.5" />Write</TabsTrigger>
            <TabsTrigger value="preview"><Eye className="mr-1.5 h-3.5 w-3.5" />Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="write">
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={20}
              placeholder="Write your article in Markdown…"
              className="font-mono text-sm"
            />
          </TabsContent>
          <TabsContent value="preview">
            <div className="prose prose-sm max-w-none rounded border p-4 min-h-[200px] bg-background">
              {content
                ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ img: KbImg as never }}>{content}</ReactMarkdown>
                : <p className="text-muted-foreground italic">Nothing to preview yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isPublished}
          onChange={e => setIsPublished(e.target.checked)}
          className="h-4 w-4"
        />
        Published (visible to all users)
      </label>

      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : <><Save className="mr-2 h-4 w-4" />Save</>}
        </Button>
      </div>

      <GenerateDialog
        open={genOpen}
        onOpenChange={setGenOpen}
        articleId={articleId}
        onApply={(draft, body) => {
          if (!title.trim()) setTitle(draft.title)
          if (!subtitle.trim()) setSubtitle(draft.subtitle)
          setContent(prev => (prev ? `${prev}\n\n${body}` : body))
          setGenOpen(false)
          toast.success('Draft inserted — review and edit before saving.')
        }}
        uploadImage={handleImageInsert}
      />
    </form>
  )
}

// Resolves API-served image URLs to presigned ones for inline preview.
function KbImg({ src, alt }: { src?: string; alt?: string }) {
  const [resolved, setResolved] = useState<string | null>(null)
  const isApi = typeof src === 'string' && src.includes('/kb-articles/images/')
  if (!isApi) {
    return <img src={src} alt={alt ?? ''} className="rounded border border-border max-h-72" />
  }
  if (!resolved) {
    fetch(`${src}?json=1`, { credentials: 'omit', headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j?.url) setResolved(j.url) })
      .catch(() => undefined)
    return <span className="text-xs text-muted-foreground">{alt ?? 'image'} (loading…)</span>
  }
  return <img src={resolved} alt={alt ?? ''} className="rounded border border-border max-h-72" />
}

function GenerateDialog({
  open,
  onOpenChange,
  articleId,
  onApply,
  uploadImage,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  articleId?: string
  onApply: (draft: { title: string; subtitle: string }, content: string) => void
  uploadImage: (file: File) => Promise<ImageRef | null>
}) {
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [refImages, setRefImages] = useState<ImageRef[]>([])

  async function onPickImage(file: File) {
    const ref = await uploadImage(file)
    if (ref) setRefImages(prev => [...prev, ref])
  }

  async function generate() {
    if (!prompt.trim() || busy) return
    setBusy(true)
    try {
      const result = await api<{ title: string; subtitle: string; content: string }>('/kb-articles/generate', {
        method: 'POST',
        body: {
          prompt,
          imageUrls: refImages.map(r => r.resolvedUrl),
        },
      })
      onApply({ title: result.title, subtitle: result.subtitle }, result.content)
      setPrompt('')
      setRefImages([])
    } catch (err) {
      toast.error((err as Error).message ?? 'Generation failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-600" />
            Generate article with AI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="gen-prompt">What's this article about?</Label>
            <Textarea
              id="gen-prompt"
              rows={5}
              placeholder="e.g. How to invite a teammate. Walk through Settings → Users → Invite, the role picker, and what happens when they accept."
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
            />
            {!articleId && (
              <p className="text-[11px] text-muted-foreground">
                Tip: save a draft first to attach reference screenshots — generated articles can include them inline.
              </p>
            )}
          </div>

          {articleId && (
            <div className="space-y-1.5">
              <Label className="text-xs">Reference screenshots (optional)</Label>
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer rounded border border-dashed border-border px-3 py-2 hover:bg-muted">
                <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                Add screenshot
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={async e => {
                    const file = e.target.files?.[0]
                    if (file) await onPickImage(file)
                    e.target.value = ''
                  }}
                />
              </label>
              {refImages.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {refImages.map(r => (
                    <li
                      key={r.s3Key}
                      className="inline-flex items-center gap-1 rounded border border-border bg-muted px-2 py-0.5 text-[11px]"
                    >
                      {r.filename}
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setRefImages(prev => prev.filter(x => x.s3Key !== r.s3Key))}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={generate} disabled={busy || !prompt.trim()}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating…</> : <><Sparkles className="mr-2 h-4 w-4" />Generate</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
